import * as React from 'react';

import {getChannelNameFromChannel} from 'nutshell-core/channels/helpers';
import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import type {ChannelType} from 'shells/channels/types';
import {colors} from 'shells/colors/colors';
import {SourcesIcon} from 'shells/icon/svg/sources-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import {Stack} from 'shells/layout/stack/stack';

import type {WidgetFragment as Widget} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card/card-widget-wrapper';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../core/card/helpers';
import {getAddableSources} from '../../../entity-details/sources/helpers';
import {
    useGetLeadChannels,
    useAddChannel,
    useRemoveChannel,
    useRemoveSource,
    useAddSource,
} from '../../../entity-details/sources/hooks';
import {useOpenableModal, OpenableModals} from '../../../openable-modals';
import {SOURCES_WRAPPER_LIMIT} from '../../../setup/sources/helpers';
import {useGetSources} from '../../../setup/sources/use-get-sources';

import {useDismissibleUserSetting} from '../../../../util/hooks';

import {refetchForEvent} from '../../refetch-helpers';

import {ChannelRow, SourceRowReadState} from './components';
import {ChannelSourceCreatableRow} from './components/channel-source-creatable-row';

type Props = {
    leadId: string;
    user: User | null | undefined;
    widget?: Widget;
};

export function SourcesWidgetWrapper(props: Props) {
    const [isAddingChannel, setIsAddingChannel] = React.useState<boolean>(false);
    const [isAddingSource, setIsAddingSource] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {channels, additionalSources, isLoading} = useGetLeadChannels(props.leadId);
    const {sources: allSources} = useGetSources({limit: SOURCES_WRAPPER_LIMIT});
    const {shouldShow} = useDismissibleUserSetting('has_dismissed_adtech_fx_modal');
    const {onOpen: onOpenAdTechUpsell} = useOpenableModal(OpenableModals.ADTECH_FX_INTEREST_MODAL, {
        canDismiss: true,
    });

    const addChannel = useAddChannel();
    const removeChannel = useRemoveChannel();
    const addSource = useAddSource();
    const removeSource = useRemoveSource();

    const shouldShowAdTechUpsell = shouldShow && NutClientConfig.hasAdTechFxUpsell;
    const canAccessSetup = Boolean(
        props.user && props.user.permissions && props.user.permissions.canAccessSetup
    );

    // Used as display for the overall widget, currently opting for number of
    // non-channel sources + number of channels
    const sourceTotal = additionalSources.length + channels.length;

    const isEmpty = sourceTotal === 0 && !isAddingChannel && !isAddingSource;
    const existingChannels = channels.map((channel) => channel.node.value);

    const handleEventTrigger = React.useCallback(() => {
        if (NutClientConfig.hasLeadPageRedesignM3FF) {
            refetchForEvent('sourcesUpdated');
        }
    }, []);

    const onAddChannel = (channel: ChannelType) => {
        setIsAddingChannel(false);
        addChannel({
            channel: channel,
            leadId: props.leadId,
        }).catch((e) => {
            setError(safelyGetGraphQLErrorMessage(e));
        });
    };

    const onRemoveChannel = (channel: ChannelType) => {
        removeChannel({
            channel: channel,
            leadId: props.leadId,
        }).catch((e) => {
            setError(safelyGetGraphQLErrorMessage(e));
        });
    };

    const onSelectSource = (sourceId: string) => {
        if (isAddingSource) {
            setIsAddingSource(false);
        }

        addSource({
            sourceId: sourceId,
            leadId: props.leadId,
        })
            .then(() => {
                handleEventTrigger();
            })
            .catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
    };

    const onRemoveSource = (sourceId: string) => {
        removeSource({
            leadId: props.leadId,
            sourceId: sourceId,
        })
            .then(() => {
                handleEventTrigger();
            })
            .catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
    };

    const selectMenuOptions = [
        {
            // @ts-expect-error
            icon: <SourcesIcon size={20} fill={colors.greyLt} wrapWithDiv={false} />,
            label: 'Add a channel',
            value: 'channel',
            handleSelect: () => {
                setIsAddingChannel(true);
                setIsAddingSource(false);
            },
        },
        {
            // @ts-expect-error
            icon: <SourcesIcon size={20} fill={colors.greyLt} wrapWithDiv={false} />,
            label: 'Add a source',
            value: 'source',
            handleSelect: () => {
                setIsAddingSource(true);
                setIsAddingChannel(false);
            },
        },
    ];

    const content = (
        <>
            {isAddingSource && (
                <ChannelSourceCreatableRow
                    creatableState='source'
                    sourceOptions={getAddableSources(allSources, channels, additionalSources, null)}
                    existingChannels={existingChannels}
                    canAccessSetup={canAccessSetup}
                    onAddChannel={onAddChannel}
                    onSelectSource={onSelectSource}
                    onCancel={() => {
                        setIsAddingSource(false);
                    }}
                />
            )}
            {isAddingChannel && (
                <ChannelSourceCreatableRow
                    creatableState='channel'
                    sourceOptions={getAddableSources(allSources, channels, additionalSources, null)}
                    existingChannels={existingChannels}
                    canAccessSetup={canAccessSetup}
                    onAddChannel={onAddChannel}
                    onSelectSource={onSelectSource}
                    onCancel={() => {
                        setIsAddingChannel(false);
                    }}
                />
            )}
            <Stack spacing={8}>
                {additionalSources.map((source) => (
                    <SourceRowReadState
                        key={source.id}
                        id={source.id}
                        name={source.name}
                        onRemoveSource={onRemoveSource}
                    />
                ))}
            </Stack>
            {channels.map((channel) => (
                <ChannelRow
                    key={channel.node.value}
                    name={getChannelNameFromChannel(channel.node.value)}
                    channelType={channel.node.value}
                    sources={channel.sources}
                    sourceOptions={getAddableSources(
                        allSources,
                        channels,
                        additionalSources,
                        channel.node.value
                    )}
                    canAccessSetup={canAccessSetup}
                    onSelectSource={onSelectSource}
                    onRemoveChannel={onRemoveChannel}
                    onRemoveSource={onRemoveSource}
                />
            ))}
        </>
    );

    const secondaryTitle = shouldShowAdTechUpsell && (
        <Button variant='text-primary' noPadding={true} onClick={onOpenAdTechUpsell}>
            <div className='flex align-center gap-4'>
                Advertise
                <UpgradeArrowIcon size={12} wrapWithDiv={false} color={colors.blue} />
            </div>
        </Button>
    );

    return (
        <>
            {NutClientConfig.hasLeadPageRedesignM3FF ? (
                props.widget && (
                    <CollapsibleCardWidget
                        widget={props.widget}
                        secondaryTitle={secondaryTitle}
                        addButtonProps={{
                            variant: 'multiple',
                            selectMenuOptions: selectMenuOptions,
                            isDisabled: isAddingSource || isAddingChannel,
                        }}
                        emptyStateProps={getWidgetEmptyStateProps(
                            'leadChannelsAndSources',
                            isEmpty
                        )}
                        isLoading={isLoading}
                        errorProps={{
                            error: error,
                            setError: setError,
                        }}
                        count={sourceTotal}
                        setIsAdding={(isAdding) => {
                            setIsAddingSource(isAdding);
                            setIsAddingChannel(isAdding);
                        }}
                    >
                        {content}
                    </CollapsibleCardWidget>
                )
            ) : (
                <CardWidgetWrapper
                    widgetKey='leadChannelsAndSources'
                    secondaryTitle={secondaryTitle}
                    addButtonProps={{
                        variant: 'multiple',
                        selectMenuOptions: selectMenuOptions,
                        isDisabled: isAddingSource || isAddingChannel,
                    }}
                    count={sourceTotal}
                    isLoading={isLoading}
                    isEmpty={isEmpty}
                    errorProps={{
                        error: error,
                        setError: setError,
                    }}
                >
                    {content}
                </CardWidgetWrapper>
            )}
        </>
    );
}
